import * as THREE from 'three';
import { Renderer } from './Renderer.js';
import { InputManager } from './InputManager.js';
import { SceneManager } from './SceneManager.js';
import { ProceduralAudio } from './ProceduralAudio.js';
import { FIXED_TIMESTEP, LevelTheme } from '@lucky-vr/common';
import type { RailPath } from '../gameplay/RailPath.js';

export class Engine {
  readonly renderer: Renderer;
  readonly input: InputManager;
  readonly scene: SceneManager;
  readonly audio: ProceduralAudio;
  readonly clock: THREE.Clock;

  private accumulator: number = 0;
  private onFixedUpdate: ((dt: number) => void) | null = null;
  private onRender: ((dt: number) => void) | null = null;

  constructor(container: HTMLElement, railPath: RailPath, theme: LevelTheme = LevelTheme.Countryside) {
    this.clock = new THREE.Clock();
    this.renderer = new Renderer(container);
    this.input = new InputManager(this.renderer);
    this.scene = new SceneManager(railPath, theme);
    this.audio = new ProceduralAudio();
  }

  setCallbacks(
    onFixedUpdate: (dt: number) => void,
    onRender: (dt: number) => void
  ): void {
    this.onFixedUpdate = onFixedUpdate;
    this.onRender = onRender;
  }

  start(): void {
    this.renderer.renderer.setAnimationLoop((time, frame) => {
      this.loop(frame);
    });
  }

  stop(): void {
    this.renderer.renderer.setAnimationLoop(null);
  }

  private loop(xrFrame?: XRFrame): void {
    const delta = Math.min(this.clock.getDelta(), 0.1); // cap at 100ms
    this.accumulator += delta;

    // Update input
    this.input.update(delta, xrFrame);

    // Fixed timestep updates
    while (this.accumulator >= FIXED_TIMESTEP) {
      this.onFixedUpdate?.(FIXED_TIMESTEP);
      this.accumulator -= FIXED_TIMESTEP;
    }

    // Per-frame update with real delta
    this.onRender?.(delta);

    // Render the scene
    this.renderer.render(this.scene.scene);
  }

  dispose(): void {
    this.stop();
    this.renderer.dispose();
    this.input.dispose();
  }
}
